import React, { useState } from "react";
import { Col, DatePicker, Form, message, Modal, Row } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import TextArea from "antd/es/input/TextArea";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import {
  FIRESTORE_EVENTS_TABLE,
  FIRESTORE_HIRE_REQUESTS_TABLE,
} from "../../constants/FirebaseConstants";
import { HIRE_REQUEST_STATUS_ACCEPTED } from "../../constants/HireRequestConstants";
import { getDisabledDatesForEndDateEvent } from "../../functions/Datepicker";
import { getFirebaseIDToken } from "../../functions/Firebase";
import { acceptHireRequest } from "../../urls";
import axios from "axios";
import { formatBookableSpaces, formatSpace } from "../../functions/Formatters";

const AcceptMeetingRoomModal = ({ open, onCancel, currentRequest }) => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);

  // Bij een aanvraag met meerdere ruimtes noemen we ze allemaal in de agenda,
  // anders de ruimte waarmee de aanvraag gestart is.
  const spaceLabel = !currentRequest
    ? ""
    : formatBookableSpaces(currentRequest.spaces) ||
      formatSpace(currentRequest.chosenSpace);

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        setSubmitLoading(true);

        const startDateUnix = dayjs(values.startDate).unix();
        const endDateUnix = dayjs(values.endDate).unix();

        const updateData = {
          status: HIRE_REQUEST_STATUS_ACCEPTED,
          startDate: startDateUnix,
          endDate: endDateUnix,
          updatedAt: dayjs().unix(),
        };

        // Add notes if provided
        if (values.notes) {
          updateData.notes = values.notes;
        }

        try {
          // Update the hire request
          await firestore
            .collection(FIRESTORE_HIRE_REQUESTS_TABLE)
            .doc(currentRequest.id)
            .update(updateData);

          // Create event in community agenda
          const eventData = {
            type: "booking",
            title: t("booking_event_title", {
              space: spaceLabel,
              firstName: currentRequest.firstName,
              lastName: currentRequest.lastName,
            }),
            startDate: startDateUnix,
            endDate: endDateUnix,
            description: t("booking_event_description", {
              space: spaceLabel,
              firstName: currentRequest.firstName,
              lastName: currentRequest.lastName,
            }),
            location: spaceLabel,
            private: true,
            createdBy: auth.uid,
            createdAt: dayjs().unix(),
          };

          await firestore.collection(FIRESTORE_EVENTS_TABLE).add(eventData);

          // Send acceptance email
          const idToken = await getFirebaseIDToken();
          const config = {
            headers: { Authorization: idToken },
          };

          const emailData = {
            id: currentRequest.id,
            firstName: currentRequest.firstName,
            lastName: currentRequest.lastName,
            email: currentRequest.email,
            chosenSpace: currentRequest.chosenSpace,
            startDate: startDateUnix.toString(),
            endDate: endDateUnix.toString(),
            notes: values.notes || null,
          };

          await axios.post(acceptHireRequest, emailData, config);

          message.success(t("notifications.request_accepted"));
          setSubmitLoading(false);
          closeModal();
        } catch (err) {
          console.error("Error occurred:", err);
          console.error("Error details:", err.response?.data || err.message);
          message.error(t("errors.generic"));
          setSubmitLoading(false);
        }
      })
      .catch((validationError) => {
        console.error("Form validation failed:", validationError);
        // Don't set loading to false here, as validation errors are handled by Antd
      });
  };

  const closeModal = () => {
    form.resetFields();
    setStartDate(null);
    onCancel();
  };

  return (
    <Modal
      open={open}
      onOk={handleSubmit}
      onCancel={closeModal}
      title={t("accept_booking_request", {
        space: currentRequest ? formatSpace(currentRequest.chosenSpace) : "",
      })}
      okText={t("accept_and_save")}
      cancelText={t("close")}
      confirmLoading={submitLoading}
      width={750}
    >
      {currentRequest && (
        <div>
          <p className={"mb-1"}>
            <strong>{t("original_request")}:</strong>{" "}
            {currentRequest.dateFrom || currentRequest.reservationDate}
            {!!currentRequest.dateTo && ` t/m ${currentRequest.dateTo}`}
            {!!currentRequest.timePeriod &&
              ` - ${t(currentRequest.timePeriod)}`}
          </p>
          <p>
            <strong>{t("chosen_spaces")}:</strong> {spaceLabel}
          </p>

          <Form form={form} layout="vertical" className="mt-3">
            <Row gutter={[16, 16]}>
              <Col lg={12}>
                <Form.Item
                  name="startDate"
                  label={t("start_date_time")}
                  rules={[
                    {
                      required: true,
                      message: t("form.choose_start_date"),
                    },
                  ]}
                >
                  <DatePicker
                    className="w-100"
                    locale={locale}
                    format="DD-MM-YYYY HH:mm"
                    placeholder={t("choose_start_date_time")}
                    showTime
                    minuteStep={5}
                    onChange={(date) => {
                      setStartDate(date);
                      // Auto-suggest end time (2 hours later)
                      if (date) {
                        const suggestedEndDate = dayjs(date).add(2, "hour");
                        form.setFieldsValue({
                          endDate: suggestedEndDate,
                        });
                      }
                    }}
                    disabledDate={(current) => {
                      return current < dayjs().startOf("day");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  name="endDate"
                  label={t("end_date_time")}
                  rules={[
                    {
                      required: true,
                      message: t("form.choose_end_date"),
                    },
                  ]}
                >
                  <DatePicker
                    className="w-100"
                    locale={locale}
                    format="DD-MM-YYYY HH:mm"
                    placeholder={t("choose_end_date_time")}
                    showTime
                    minuteStep={5}
                    disabledDate={(current) => {
                      return getDisabledDatesForEndDateEvent(
                        startDate,
                        current,
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col lg={24}>
                <Form.Item name="notes" label={t("notes_optional")}>
                  <TextArea
                    rows={4}
                    placeholder={t("add_notes_about_booking")}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default AcceptMeetingRoomModal;
